import ExcelJS from 'exceljs';

export interface MatrixData {
  title: string;
  headers: string[];
  rows: any[][];
}

/**
 * Optional identifiers stamped onto each sheet (client, report type, generation date)
 * so an exported workbook reads as a presentable report rather than a raw data dump.
 */
export interface ExportMetadata {
  clientName?: string;
  reportTitle?: string;
  generatedAt?: Date;
}

// Brand palette (matches the app's primary blue #1993e5)
const HEADER_FILL = 'FF1993E5';
const TITLE_COLOR = 'FF0F2A43';
const SUBTITLE_COLOR = 'FF6B7280';
const BORDER_COLOR = 'FFBFC7D1';

/**
 * Sanitize sheet name for Excel compatibility
 * - Max 31 characters
 * - Cannot contain: \ / * ? : [ ]
 */
const sanitizeSheetName = (name: string): string => {
  return name
    // eslint-disable-next-line no-useless-escape
    .replace(/[\\/*?:\[\]]/g, '')
    .trim()
    .substring(0, 31);
};

/**
 * Strip citation markers from cell values
 * Handles strings, CitationText objects, value objects, and other values
 *
 * Supported formats:
 * - Plain strings: "some text"
 * - CitationText: { text: "...", citations: [] }
 * - Value objects: { value: "...", citations: [] }
 * - Nested content: { content: { text: "...", citations: [] } }
 * - Citation refs: { docId: "doc_1", docName: "...", page: "1" }
 */
const stripCitations = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }

  // Handle primitives
  if (typeof value === 'string') {
    return value.replace(/\[CITE:\s*"[^"]*"\]/g, '').trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // Handle arrays - join extracted values
  if (Array.isArray(value)) {
    const extracted = value.map(v => stripCitations(v)).filter(v => v !== '');
    return extracted.join(', ');
  }

  // Must be an object at this point
  if (typeof value !== 'object') {
    return String(value);
  }

  // Priority order for extracting meaningful text from objects:

  // 1. Direct value property (comparison matrix cells)
  if (value.value !== undefined) {
    return stripCitations(value.value);
  }

  // 2. Direct text property (CitationText objects)
  if (value.text !== undefined) {
    return stripCitations(value.text);
  }

  // 3. Content property (nested CitationText)
  if (value.content !== undefined) {
    return stripCitations(value.content);
  }

  // 4. Common display properties
  const displayProps = ['label', 'name', 'title', 'description', 'summary', 'finding', 'action', 'rationale'];
  for (const prop of displayProps) {
    if (value[prop] !== undefined) {
      return stripCitations(value[prop]);
    }
  }

  // 5. Handle citation reference objects - extract the quote or docName
  if (value.docId !== undefined || value.docName !== undefined) {
    if (value.quote) {
      return stripCitations(value.quote);
    }
    // Don't display citation refs as data
    return '';
  }

  // 6. Try to find ANY string property as a last resort
  const stringProps = Object.entries(value).filter(([key, val]) =>
    typeof val === 'string' &&
    !['docId', 'docName', 'page', 'section', 'sourceDocId', 'documentId', 'id', 'type'].includes(key)
  );

  if (stringProps.length > 0) {
    // Return the first meaningful string property found
    return stripCitations(stringProps[0][1]);
  }

  // 7. Try nested objects
  const objectProps = Object.entries(value).filter(([key, val]) =>
    typeof val === 'object' &&
    val !== null &&
    !Array.isArray(val) &&
    key !== 'citations'
  );

  if (objectProps.length > 0) {
    const result = stripCitations(objectProps[0][1]);
    if (result) {
      return result;
    }
  }

  // Last resort - return empty string to avoid [object Object]
  return '';
};

/**
 * Normalize row data - handles both array and object row formats
 * Comparison matrices use object rows: { "Header1": { value: "...", citations: [] }, ... }
 * Some workflows use array rows: [cell1, cell2, cell3, ...]
 */
const normalizeRow = (row: any, headers: string[], matrixTitle?: string): string[] => {
  // Cells-array shape from yoy_renewal add-ons + policy_comparison:
  //   { cells: [{ column, value, citations[] }, ...] }
  // Convert it to a {column: value} dict so the object-row path below handles it.
  if (row && typeof row === 'object' && !Array.isArray(row) && Array.isArray(row.cells)) {
    const dict: Record<string, any> = {};
    for (const c of row.cells) {
      if (c && typeof c === 'object' && typeof c.column === 'string') {
        dict[c.column] = c.value ?? '';
      }
    }
    row = dict;
  }

  if (Array.isArray(row)) {
    return row.map((cell, idx) => {
      const result = stripCitations(cell);
      // Debug: Log if we get [object Object]
      if (result === '[object Object]' || String(cell) === '[object Object]') {
        console.warn('📊 Excel Export - [object Object] detected in array row!', {
          matrixTitle,
          cellIndex: idx,
          cellType: typeof cell,
          cellValue: JSON.stringify(cell).substring(0, 300)
        });
      }
      return result;
    });
  }

  // Object row - extract values by header keys
  if (typeof row === 'object' && row !== null) {
    return headers.map(header => {
      // Try exact match first
      if (row[header] !== undefined) {
        const result = stripCitations(row[header]);
        // Debug: Log if we get [object Object]
        if (result === '[object Object]' || String(row[header]) === '[object Object]') {
          console.warn('📊 Excel Export - [object Object] detected in object row!', {
            matrixTitle,
            header,
            valueType: typeof row[header],
            valueContent: JSON.stringify(row[header]).substring(0, 300)
          });
        }
        return result;
      }

      // Try case-insensitive match
      const key = Object.keys(row).find(
        k => k.toLowerCase() === header.toLowerCase()
      );
      if (key) {
        return stripCitations(row[key]);
      }

      // Try matching without special characters (e.g., "🔗 Citations" -> "Citations")
      const normalizedHeader = header.replace(/[^\w\s]/g, '').trim().toLowerCase();
      const normalizedKey = Object.keys(row).find(k =>
        k.replace(/[^\w\s]/g, '').trim().toLowerCase() === normalizedHeader
      );
      if (normalizedKey) {
        return stripCitations(row[normalizedKey]);
      }

      return '';
    });
  }

  return headers.map(() => '');
};

interface ExcelCellValue {
  value: string | number;
  numFmt?: string;
  /** Original text kept as a cell note when an annotation was dropped to make the cell numeric. */
  note?: string;
}

const SUFFIX_MULTIPLIERS: Record<string, number> = { k: 1e3, m: 1e6, mm: 1e6, b: 1e9, bn: 1e9 };

// Whole-cell number: optional sign, optional $/USD, digits (comma-grouped or not), optional
// decimals, optional K/M/B (currency only) or %, optional accounting parentheses.
const NUMBER_PATTERN = /^(\()?\s*([+-])?\s*(\$|USD\s?)?\s*([+-])?\s*(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?\s*(k|mm|m|bn|b|%)?\s*(\))?$/i;

/**
 * Convert a cell's display text into a real Excel number when it is one, so exported
 * columns can be summed / pivoted. Anything ambiguous (dates, ranges, IDs with leading
 * zeros, "$1M / $2M") stays text. A currency amount followed only by a parenthetical
 * annotation — "$6,039.00 (At-Bay)", "+$3,501 (+58%)" — becomes the number, with the
 * full original text preserved as a cell note.
 */
export const toExcelValue = (text: string): ExcelCellValue => {
  const raw = text.trim();
  if (!raw) return { value: '' };

  const parse = (s: string): ExcelCellValue | null => {
    const m = s.replace(/−/g, '-').match(NUMBER_PATTERN);
    if (!m) return null;
    const [, openParen, sign1, currency, sign2, intPart, decPart, suffixRaw, closeParen] = m;
    if (!!openParen !== !!closeParen) return null;
    if (sign1 && sign2) return null;
    const suffix = suffixRaw?.toLowerCase();
    const isPercent = suffix === '%';
    // K/M/B only reads as a magnitude when it's money ("$1.2M"); "5M" alone is too ambiguous
    if (suffix && !isPercent && !currency) return null;
    const digits = intPart.replace(/,/g, '');
    // Leading-zero codes and bare 7+ digit identifiers (policy/claim numbers) must stay text
    if (!currency && !isPercent && !decPart && !intPart.includes(',') && (digits.length >= 7 || (digits.length > 1 && digits.startsWith('0')))) return null;
    if (digits.length > 15) return null;

    let num = parseFloat(digits + (decPart || ''));
    if (suffix && !isPercent) num *= SUFFIX_MULTIPLIERS[suffix];
    if (openParen || sign1 === '-' || sign2 === '-') num = -num;

    const decimals = suffix && !isPercent ? 0 : (decPart ? decPart.length - 1 : 0);
    const dec = decimals > 0 ? `.${'0'.repeat(Math.min(decimals, 4))}` : '';
    if (isPercent) return { value: num / 100, numFmt: `0${dec}%` };
    if (currency) return { value: num, numFmt: `"$"#,##0${dec};-"$"#,##0${dec}` };
    if (intPart.includes(',') || dec) return { value: num, numFmt: `#,##0${dec}` };
    return { value: num };
  };

  const strict = parse(raw);
  if (strict) return strict;

  const annotated = raw.match(/^(.+?)\s+\(([^()]+)\)$/);
  if (annotated && /\$|USD/i.test(annotated[1])) {
    const lead = parse(annotated[1]);
    if (lead) return { ...lead, note: raw };
  }

  return { value: raw };
};

const THIN_BORDER = {
  top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
  left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
  bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
  right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
};

/**
 * Export matrices to a styled Excel file — one sheet per matrix, each with a title
 * banner, optional identifier subtitle, a colored header row, thin borders boxing the
 * whole table, a frozen header, auto-filter, tuned column widths, and print setup
 * (landscape, fit-to-1-page-wide, header repeated on each printed page).
 *
 * Async because ExcelJS writes the workbook to a buffer asynchronously.
 */
export const exportMatricesToExcel = async (
  matrices: MatrixData[],
  filename: string,
  metadata?: ExportMetadata
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Risk Ninja';
  workbook.created = metadata?.generatedAt || new Date();

  const usedNames = new Set<string>();

  // Build the identifier subtitle line once (e.g. "Acme Corp • Loss Run Analysis • Jun 18, 2026")
  const subtitleParts: string[] = [];
  if (metadata?.clientName) subtitleParts.push(metadata.clientName);
  if (metadata?.reportTitle) subtitleParts.push(metadata.reportTitle);
  subtitleParts.push(`Generated ${(metadata?.generatedAt || new Date()).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  })}`);
  const subtitle = subtitleParts.join('  •  ');

  for (const matrix of matrices) {
    // Generate unique sheet name
    let sheetName = sanitizeSheetName(matrix.title || 'Sheet');
    const baseName = sheetName;
    let counter = 1;
    while (usedNames.has(sheetName)) {
      const suffix = ` (${counter})`;
      sheetName = baseName.substring(0, 31 - suffix.length) + suffix;
      counter++;
    }
    usedNames.add(sheetName);

    const headers = matrix.headers;
    const colCount = Math.max(headers.length, 1);
    const cleanRows = matrix.rows.map(row => normalizeRow(row, headers, matrix.title));

    const ws = workbook.addWorksheet(sheetName, {
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        horizontalCentered: true,
        margins: { left: 0.5, right: 0.5, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 },
      },
      views: [{ showGridLines: false }],
    });

    // --- Title banner (row 1) ---
    ws.mergeCells(1, 1, 1, colCount);
    const titleCell = ws.getCell(1, 1);
    titleCell.value = matrix.title || 'Report';
    titleCell.font = { bold: true, size: 16, color: { argb: TITLE_COLOR } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
    ws.getRow(1).height = 24;

    // --- Identifier subtitle (row 2) ---
    ws.mergeCells(2, 1, 2, colCount);
    const subtitleCell = ws.getCell(2, 1);
    subtitleCell.value = subtitle;
    subtitleCell.font = { italic: true, size: 10, color: { argb: SUBTITLE_COLOR } };
    subtitleCell.alignment = { vertical: 'middle', horizontal: 'left' };

    const headerRowIndex = 4; // row 3 left blank as a spacer
    const headerRow = ws.getRow(headerRowIndex);
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
      cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      cell.border = THIN_BORDER;
    });
    headerRow.height = 20;

    // --- Data rows (boxed with thin borders) ---
    cleanRows.forEach((row, r) => {
      const excelRow = ws.getRow(headerRowIndex + 1 + r);
      for (let c = 0; c < colCount; c++) {
        const cell = excelRow.getCell(c + 1);
        const { value, numFmt, note } = toExcelValue(row[c] ?? '');
        cell.value = value;
        if (numFmt) cell.numFmt = numFmt;
        if (note) cell.note = note;
        cell.alignment = { vertical: 'top', horizontal: typeof value === 'number' ? 'right' : 'left', wrapText: true };
        cell.border = THIN_BORDER;
        if (r % 2 === 1) {
          // Subtle zebra striping for readability
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F8FB' } };
        }
      }
    });

    // --- Column widths (auto-fit, clamped) ---
    headers.forEach((header, colIndex) => {
      const maxContentLength = Math.max(
        header.length,
        ...cleanRows.map(row => (row[colIndex] || '').length)
      );
      ws.getColumn(colIndex + 1).width = Math.min(Math.max(maxContentLength + 2, 12), 55);
    });

    // --- Freeze header, auto-filter, repeat header row when printed ---
    ws.views = [{ state: 'frozen', ySplit: headerRowIndex, showGridLines: false }];
    if (cleanRows.length > 0) {
      ws.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: colCount },
      };
    }
    ws.pageSetup.printTitlesRow = `${headerRowIndex}:${headerRowIndex}`;
  }

  // Write to a buffer and trigger a browser download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const safeName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Convert an array of objects to matrix format
 */
const convertObjectArrayToMatrix = (title: string, data: any[]): MatrixData | null => {
  if (!Array.isArray(data) || data.length === 0) return null;

  // Get headers from first object's keys
  const firstItem = data[0];
  if (typeof firstItem !== 'object' || firstItem === null || Array.isArray(firstItem)) {
    return null;
  }

  const headers = Object.keys(firstItem);
  if (headers.length === 0) return null;

  // Convert to rows
  const rows = data.map(item => headers.map(h => item[h]));

  return {
    title,
    headers,
    rows
  };
};

/**
 * Format a field name to a readable title
 */
const formatFieldName = (fieldName: string): string => {
  // Custom mappings for better titles
  const customTitles: Record<string, string> = {
    'claimLineItems': 'All Claim Lines',
    'categoryBreakdown': 'Category Breakdown',
    'severityBreakdown': 'Severity Breakdown',
    'topClaims': 'Top Claims',
    'costBenefitAnalysis': 'Cost Benefit Analysis',
    'riskMitigationStrategies': 'Risk Mitigation Strategies',
    'renewalRecommendations': 'Renewal Recommendations',
    'nonStandardProvisions': 'Non-Standard Provisions',
    'clauseWordingComparison': 'Clause Wording Comparison'
  };

  if (customTitles[fieldName]) {
    return customTitles[fieldName];
  }

  return fieldName
    // Insert space before capital letters
    .replace(/([A-Z])/g, ' $1')
    // Replace underscores with spaces
    .replace(/_/g, ' ')
    // Capitalize first letter
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Extract matrices from report data
 * Handles various matrix field names used in different workflow types
 */
export const extractMatricesFromReport = (reportData: any): MatrixData[] => {
  const matrices: MatrixData[] = [];

  if (!reportData) return matrices;

  // Fields that contain arrays of matrix objects (with title, headers, rows)
  const matrixArrayFields = [
    'analysisMatrices',
    'coverageAnalysisMatrices',
    'comparisonMatrices',
    'claimMatrices',
    'riskMatrices'
  ];

  for (const field of matrixArrayFields) {
    const fieldData = reportData[field];
    if (Array.isArray(fieldData)) {
      for (const matrix of fieldData) {
        if (matrix.title && matrix.headers && Array.isArray(matrix.rows)) {
          matrices.push({
            title: matrix.title,
            headers: matrix.headers,
            rows: matrix.rows
          });
        }
      }
    }
  }

  // Fields that are arrays of objects that can be converted to tables
  const tableArrayFields = [
    'costBenefitAnalysis',
    'riskMitigationStrategies',
    'recommendations',
    'renewalRecommendations',
    // Loss run specific fields
    'claimLineItems',
    'categoryBreakdown',
    'severityBreakdown',
    'topClaims'
  ];

  for (const field of tableArrayFields) {
    const fieldData = reportData[field];
    // Handle nested recommendations structure
    const dataArray = fieldData?.recommendations || fieldData;

    if (Array.isArray(dataArray) && dataArray.length > 0) {
      const matrix = convertObjectArrayToMatrix(formatFieldName(field), dataArray);
      if (matrix) {
        matrices.push(matrix);
      }
    }
  }

  // Handle single matrix objects (like riskManagementMatrix)
  if (reportData.riskManagementMatrix) {
    const rmm = reportData.riskManagementMatrix;
    if (rmm.headers && Array.isArray(rmm.rows)) {
      matrices.push({
        title: rmm.title || 'Risk Management Matrix',
        headers: rmm.headers,
        rows: rmm.rows
      });
    } else if (rmm.recommendations && Array.isArray(rmm.recommendations)) {
      // Convert recommendations array to matrix
      const matrix = convertObjectArrayToMatrix(
        rmm.title || 'Risk Management Matrix',
        rmm.recommendations
      );
      if (matrix) {
        matrices.push(matrix);
      }
    }
  }

  // Handle clauseWordingComparison if it has comparisons array
  if (reportData.clauseWordingComparison?.comparisons &&
      Array.isArray(reportData.clauseWordingComparison.comparisons) &&
      reportData.clauseWordingComparison.comparisons.length > 0) {
    const matrix = convertObjectArrayToMatrix(
      'Clause Wording Comparison',
      reportData.clauseWordingComparison.comparisons
    );
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // Handle nonStandardProvisions if it has provisions array
  if (reportData.nonStandardProvisions?.provisions &&
      Array.isArray(reportData.nonStandardProvisions.provisions) &&
      reportData.nonStandardProvisions.provisions.length > 0) {
    const matrix = convertObjectArrayToMatrix(
      'Non-Standard Provisions',
      reportData.nonStandardProvisions.provisions
    );
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // === Loss Run Analyzer Specific Fields ===

  // Claims array (main claims data)
  if (reportData.claims && Array.isArray(reportData.claims) && reportData.claims.length > 0) {
    const matrix = convertObjectArrayToMatrix('All Claims', reportData.claims);
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // Document Manifest
  if (reportData.documentManifest && Array.isArray(reportData.documentManifest) && reportData.documentManifest.length > 0) {
    const matrix = convertObjectArrayToMatrix('Document Manifest', reportData.documentManifest);
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // Validation Results
  if (reportData.validationResults && Array.isArray(reportData.validationResults) && reportData.validationResults.length > 0) {
    const matrix = convertObjectArrayToMatrix('Cross-Foot Validation', reportData.validationResults);
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // Claim Development
  if (reportData.claimDevelopment && Array.isArray(reportData.claimDevelopment) && reportData.claimDevelopment.length > 0) {
    const matrix = convertObjectArrayToMatrix('Claim Development', reportData.claimDevelopment);
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // Frequency Analysis sub-arrays
  if (reportData.frequencyAnalysis) {
    const freq = reportData.frequencyAnalysis;

    // Claims Per Period
    if (freq.claimsPerPeriod && Array.isArray(freq.claimsPerPeriod) && freq.claimsPerPeriod.length > 0) {
      const matrix = convertObjectArrayToMatrix('Claims Per Period', freq.claimsPerPeriod);
      if (matrix) {
        matrices.push(matrix);
      }
    }

    // Claims By Line of Business
    if (freq.claimsByLOB && Array.isArray(freq.claimsByLOB) && freq.claimsByLOB.length > 0) {
      const matrix = convertObjectArrayToMatrix('Claims By Line of Business', freq.claimsByLOB);
      if (matrix) {
        matrices.push(matrix);
      }
    }

    // Claims By State
    if (freq.claimsByState && Array.isArray(freq.claimsByState) && freq.claimsByState.length > 0) {
      const matrix = convertObjectArrayToMatrix('Claims By State', freq.claimsByState);
      if (matrix) {
        matrices.push(matrix);
      }
    }
  }

  // Severity Analysis distribution
  if (reportData.severityAnalysis?.distribution &&
      Array.isArray(reportData.severityAnalysis.distribution) &&
      reportData.severityAnalysis.distribution.length > 0) {
    const matrix = convertObjectArrayToMatrix('Severity Distribution', reportData.severityAnalysis.distribution);
    if (matrix) {
      matrices.push(matrix);
    }
  }

  // === YOY Renewal opt-in add-on sections (Report Options step) ===
  // Each emits one Excel sheet so the user can audit / pivot the data.
  if (reportData.reportAddOns) {
    const addOns: any = reportData.reportAddOns;

    // Schedules — each schedule object has its own headers + rows
    // Tolerate every shape Gemini has produced (canonical, double-wrap, extra-array)
    let scheduleList: any[] = [];
    if (Array.isArray(addOns.schedules)) {
      scheduleList = addOns.schedules.length > 0 && addOns.schedules.every((s: any) => Array.isArray(s))
        ? addOns.schedules.flat()
        : addOns.schedules;
    } else if (addOns.schedules && Array.isArray(addOns.schedules.schedules)) {
      scheduleList = addOns.schedules.schedules;
    }
    for (const sched of scheduleList) {
      if (sched && Array.isArray(sched.headers) && Array.isArray(sched.rows) && sched.rows.length > 0) {
        const title = sched.policyDocName
          ? `${sched.scheduleType || 'Schedule'} — ${sched.policyDocName}`
          : (sched.scheduleType || 'Schedule');
        matrices.push({ title, headers: sched.headers, rows: sched.rows });
      }
    }

    // Coverage Details — multiple sub-matrices, each in its own sheet
    const cds = addOns.coverageDetails;
    if (cds && typeof cds === 'object') {
      const matrixSubFields: Array<[string, string]> = [
        ['deductiblesAndRetentions', 'Deductibles & Retentions'],
        ['subLimits', 'Sub-Limits'],
        ['enhancementEndorsementLimits', 'Enhancement Endorsement Limits'],
        ['coverageTriggers', 'Coverage Triggers'],
      ];
      for (const [key, label] of matrixSubFields) {
        const m = cds[key];
        if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
          matrices.push({ title: label, headers: m.headers, rows: m.rows });
        }
      }
      // exclusionsAdded / exclusionsRemoved — flatten RichText {text, citations} → strings
      const toExclusionMatrix = (key: string, label: string) => {
        const arr = cds[key];
        if (Array.isArray(arr) && arr.length > 0) {
          const rows = arr.map((e: any) => [typeof e === 'string' ? e : (e?.text || '')]);
          matrices.push({ title: label, headers: ['Exclusion'], rows });
        }
      };
      toExclusionMatrix('exclusionsAdded', 'Exclusions Added');
      toExclusionMatrix('exclusionsRemoved', 'Exclusions Removed');
      // Modified exclusions — flatten the {exclusion, expiringLanguage, renewalLanguage} structure
      if (Array.isArray(cds.exclusionsModified) && cds.exclusionsModified.length > 0) {
        const rows = cds.exclusionsModified.map((m: any) => [
          m?.exclusion || '',
          typeof m?.expiringLanguage === 'string' ? m.expiringLanguage : (m?.expiringLanguage?.text || ''),
          typeof m?.renewalLanguage === 'string' ? m.renewalLanguage : (m?.renewalLanguage?.text || ''),
        ]);
        matrices.push({
          title: 'Exclusions Modified',
          headers: ['Exclusion', 'Expiring Language', 'Renewal Language'],
          rows,
        });
      }
      // Key Definitions
      if (Array.isArray(cds.keyDefinitions) && cds.keyDefinitions.length > 0) {
        const rows = cds.keyDefinitions.map((kd: any) => [
          kd?.term || '',
          typeof kd?.expiringDefinition === 'string' ? kd.expiringDefinition : (kd?.expiringDefinition?.text || ''),
          typeof kd?.renewalDefinition === 'string' ? kd.renewalDefinition : (kd?.renewalDefinition?.text || ''),
          kd?.materialDifference ? 'Yes' : 'No',
        ]);
        matrices.push({
          title: 'Key Definitions',
          headers: ['Term', 'Expiring Definition', 'Renewal Definition', 'Material Difference'],
          rows,
        });
      }
    }

    // Compliance & Contractual — coiRequirements + contractualObligations matrices
    const cc = addOns.complianceContractual;
    if (cc && typeof cc === 'object') {
      const ccFields: Array<[string, string]> = [
        ['coiRequirements', 'COI Requirements'],
        ['contractualObligations', 'Contractual Obligations'],
      ];
      for (const [key, label] of ccFields) {
        const m = cc[key];
        if (m && Array.isArray(m.headers) && Array.isArray(m.rows) && m.rows.length > 0) {
          matrices.push({ title: label, headers: m.headers, rows: m.rows });
        }
      }
    }
  }

  return matrices;
};
